import React, { useState, useEffect } from 'react';
import { Axios } from '@helpers/ApiService';
import { useNavigate } from 'react-router-dom';
import { onboardingStepId, storeSetupSubsteps } from '@helpers/Constants';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { ButcherHeader } from '@components/ButcherHeader';
import { useToast } from '@contexts/Toast';
import { CustomerOrderService } from '@services/CustomerService';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { useInputValidation } from '@hooks/useInputValidation';

export const TaxDetails: React.FC = () => {
  const { updateOnboardingStep } = useLocalStorage();
  const [loading, setLoading] = useState<boolean>(true);
  const [isStepComplete, setIsStepComplete] = useState<boolean>(false);

  const navigate = useNavigate();
  const { showToastMessage } = useToast();

  // Use input validation hooks for tax rates
  const serviceTaxValidation = useInputValidation('currency', '');
  const productTaxValidation = useInputValidation('currency', '');

  // Fetch tax rates on component mount
  useEffect(() => {
    const fetchTaxRates = async () => {
      try {
        setLoading(true);
        const response = await Axios.post(ApiEndpoints.FETCH_TAX_RATES);

        if (response.data.status === 'success' && response.data.data) {
          const { serviceTax, productTax } = response.data.data;
          serviceTaxValidation.setValue(serviceTax || '');
          productTaxValidation.setValue(productTax || '');
        }

        // Check if the tax step is already complete
        const onboardingSteps = localStorage.getItem('onboardingSteps');
        if (onboardingSteps) {
          const steps = JSON.parse(onboardingSteps);
          const taxStep = steps.find(
            (step: any) => step.stepsId === onboardingStepId.TAX_RATE,
          );
          setIsStepComplete(taxStep?.isComplete || false);
        }
      } catch (error) {
        console.error('Error fetching tax rates:', error);
        showToastMessage('Failed to load tax rates', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchTaxRates();
  }, []);

  // Save handler – calls API, updates step completion, does NOT navigate
  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();

    // Validate both tax rates using the validation hooks
    if (!serviceTaxValidation.value && !productTaxValidation.value) {
      showToastMessage(
        'Both service and product tax rates are required',
        'error',
      );
      return;
    }

    if (!serviceTaxValidation.value) {
      showToastMessage('Service tax rate is required', 'error');
      return;
    }

    if (!productTaxValidation.value) {
      showToastMessage('Product tax rate is required', 'error');
      return;
    }

    // Check if there are validation errors
    if (!serviceTaxValidation.isValid || !productTaxValidation.isValid) {
      showToastMessage('Please fix validation errors before saving', 'error');
      return;
    }

    const serviceNumericRate = parseFloat(serviceTaxValidation.value);
    const productNumericRate = parseFloat(productTaxValidation.value);

    if (
      isNaN(serviceNumericRate) ||
      serviceNumericRate < 0 ||
      serviceNumericRate > 100
    ) {
      showToastMessage('Service tax rate must be between 0 and 100', 'error');
      return;
    }

    if (
      isNaN(productNumericRate) ||
      productNumericRate < 0 ||
      productNumericRate > 100
    ) {
      showToastMessage('Product tax rate must be between 0 and 100', 'error');
      return;
    }

    const payload = {
      serviceTax: serviceNumericRate,
      productTax: productNumericRate,
    };

    try {
      const response = await Axios.post(ApiEndpoints.UPSERT_TAX_RATES, payload);

      if (response.data.status === 'success') {
        const butcherDetails = localStorage.getItem('butcherDetails');

        let details = butcherDetails ? JSON.parse(butcherDetails) : {};
        details = {
          ...details,
          serviceTax: serviceNumericRate,
          productTax: productNumericRate,
        };
        // Update the butcherDetails in local storage
        CustomerOrderService.setButcherDetails(details);
        showToastMessage('Tax rates saved successfully', 'success');

        updateOnboardingStep(onboardingStepId.TAX_RATE);
        setIsStepComplete(true); // enable the Next button
      } else {
        showToastMessage(
          'Failed to save tax rates. Please try again.',
          'error',
        );
      }
    } catch (error) {
      showToastMessage('Failed to save tax rates. Please try again.', 'error');
    }
  };

  // Next handler – only navigates, no API call
  const handleNext = () => {
    navigate(FrontendRoutes.SPECIES);
  };

  const renderContent = (
    <>
      <div>
        {/* Breadcrumbs */}
        <Breadcrumbs
          title="Configure Settings"
          activeMainNav="Store"
          activeStep={3}
          subNavSteps={storeSetupSubsteps}
          fromSettings={false}
        />
        <div className="mt-4">
          <h2 className="text-lg font-medium mb-4">
            Service and Product Tax Rates
          </h2>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="text-gray-500">Loading tax rates...</div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-[#F1F8ED] rounded-lg">
              <span className="text-lg font-medium text-gray-700">
                Service Tax Rate
              </span>
              {/* input for service tax rate */}
              <div className="flex flex-col items-end">
                <input
                  type="text"
                  value={serviceTaxValidation.value}
                  onChange={serviceTaxValidation.handleChange}
                  onKeyPress={serviceTaxValidation.handleKeyPress}
                  placeholder="Tax Rate %"
                  className="border border-gray-300 rounded-md p-2 w-36 text-right"
                  disabled={loading}
                />
                {serviceTaxValidation.error && (
                  <span className="text-red-500 text-xs mt-1">
                    {serviceTaxValidation.error}
                  </span>
                )}
              </div>
            </div>

            <div className="flex justify-between items-center p-4  bg-[#F1F8ED] rounded-lg">
              <span className="text-lg font-medium text-gray-700">
                Product Tax Rate
              </span>
              <div className="flex flex-col items-end">
                <input
                  type="text"
                  value={productTaxValidation.value}
                  onChange={productTaxValidation.handleChange}
                  onKeyPress={productTaxValidation.handleKeyPress}
                  placeholder="Tax Rate %"
                  className="border border-gray-300 rounded-md p-2 w-36 text-right"
                  disabled={loading}
                />
                {productTaxValidation.error && (
                  <span className="text-red-500 text-xs mt-1">
                    {productTaxValidation.error}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="w-screen h-screen flex flex-col">
      <div className="flex-1 overflow-auto flex justify-center items-start">
        <div className="w-11/12 p-4">
          <ButcherHeader isOnBoarding={true} />
          {renderContent}
        </div>
      </div>

      {/* Fixed Save & Next Buttons at Bottom */}
      <div className="w-full p-4">
        <div className="w-full gap-4 mx-auto flex justify-between xl:justify-end lg:justify-end">
          {/* Save button – calls API */}
          <button
            onClick={handleSave}
            disabled={
              !serviceTaxValidation.value ||
              !productTaxValidation.value ||
              !serviceTaxValidation.isValid ||
              !productTaxValidation.isValid ||
              loading
            }
            className="w-[50%] xl:max-w-[15%] lg:max-w-[15%] px-6 py-1.5 rounded-md text-sm font-medium text-primary bg-white hover:bg-primary border border-primary hover:text-white focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
          >
            Save
          </button>

          {/* Next button – only navigation, enabled only if step is complete */}
          <button
            onClick={handleNext}
            disabled={!isStepComplete}
            className="w-[50%] xl:max-w-[15%] lg:max-w-[15%] px-6 py-1.5 rounded-md text-sm font-medium text-white bg-primary hover:bg-primary focus:outline-none flex items-center justify-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};